/**
 * ShareDialog Component
 *
 * Dialog for creating and sharing public links to content and views.
 * Only available for items with PUBLIC status.
 *
 * Features:
 * - Expiration selection
 * - Optional password protection
 * - Optional usage limits
 * - Automatic clipboard copy
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { publicLinksService } from '../../services/public-links';
import { useOrganization } from '../../context/OrganizationContext';

export interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  itemId: string;
  itemType: 'content' | 'view';
  itemTitle?: string;
}

interface ShareLinkResponse {
  linkId: string;
  fullUrl: string;
  expiresAt?: string;
  createdAt: string;
  hasPassword: boolean;
  maxUses?: number;
  accessCount: number;
}

export const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  onClose,
  itemId,
  itemType,
  itemTitle = 'this item',
}) => {
  const { currentTeamId } = useOrganization();

  // Form state
  const [expiration, setExpiration] = useState<string>('2592000'); // 30 days default
  const [enablePassword, setEnablePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [enableMaxUses, setEnableMaxUses] = useState(false);
  const [maxUses, setMaxUses] = useState(25);
  const [description, setDescription] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedLink, setGeneratedLink] = useState<ShareLinkResponse | null>(null);
  const [copied, setCopied] = useState(false);

  // Password validation
  const validatePasswordLength = (pwd: string): boolean => pwd.length >= 6;
  const isPasswordValid = validatePasswordLength(password);

  const passwordRequirements = [
    { label: 'At least 6 characters', valid: validatePasswordLength(password) },
  ];

  const handleGenerateLink = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate password if enabled
      if (enablePassword && password.length < 6) {
        setError('Password must be at least 6 characters long');
        setLoading(false);
        return;
      }

      // Validate maxUses if enabled
      if (enableMaxUses && maxUses < 1) {
        setError('Usage limit must be at least 1');
        setLoading(false);
        return;
      }

      const requestBody: any = {
        team_id: currentTeamId,
        expiresInSeconds: expiration === 'never' ? undefined : parseInt(expiration, 10),
      };

      if (enablePassword && password) {
        requestBody.password = password;
      }

      if (enableMaxUses) {
        requestBody.maxUses = maxUses;
      }

      if (description) {
        requestBody.description = description;
      }

      // Generate link via service
      const linkData = itemType === 'content'
        ? await publicLinksService.generateContentLink(itemId, requestBody)
        : await publicLinksService.generateViewLink(itemId, requestBody);

      setGeneratedLink(linkData);

      // Automatically copy to clipboard
      await navigator.clipboard.writeText(linkData.fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err: any) {
      console.error('Error generating link:', err);
      const errorMessage = err.response?.data?.error?.message
        || err.message
        || 'Failed to generate public link';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (generatedLink) {
      await navigator.clipboard.writeText(generatedLink.fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleClose = () => {
    // Reset form
    setExpiration('2592000');
    setEnablePassword(false);
    setPassword('');
    setShowPassword(false);
    setEnableMaxUses(false);
    setMaxUses(25);
    setGeneratedLink(null);
    setError(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Share {itemType === 'content' ? 'Content' : 'View'}
      </DialogTitle>

      <DialogContent>
        {!generatedLink ? (
          // Link configuration form
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Create a public link to share "{itemTitle}" with anyone.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Description */}
            <TextField
              fullWidth
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Link for Customer X, Q4 Report Share"
              helperText="Help identify this link's purpose and track usage"
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 200 }}
            />

            {/* Expiration */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Link Expiration</InputLabel>
              <Select
                value={expiration}
                onChange={(e) => setExpiration(e.target.value)}
                label="Link Expiration"
              >
                <MenuItem value="3600">1 hour</MenuItem>
                <MenuItem value="86400">24 hours</MenuItem>
                <MenuItem value="604800">7 days</MenuItem>
                <MenuItem value="2592000">30 days</MenuItem>
                <MenuItem value="never">Never expires</MenuItem>
              </Select>
            </FormControl>

            {/* Password Protection */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={enablePassword}
                  onChange={(e) => setEnablePassword(e.target.checked)}
                />
              }
              label="Password protect this link"
              sx={{ mb: 1 }}
            />

            {enablePassword && (
              <>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  sx={{ mb: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {password.length > 0 && (
                  <Box sx={{ mb: 2, mt: 1 }}>
                    {passwordRequirements.map((req, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {req.valid ? (
                          <CheckCircleIcon fontSize="small" color="success" sx={{ mr: 1 }} />
                        ) : (
                          <CancelIcon fontSize="small" color="error" sx={{ mr: 1 }} />
                        )}
                        <Typography
                          variant="body2"
                          color={req.valid ? 'success.main' : 'error.main'}
                        >
                          {req.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}

            {/* Usage Limit */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableMaxUses}
                  onChange={(e) => setEnableMaxUses(e.target.checked)}
                />
              }
              label="Limit number of uses"
              sx={{ mb: 1 }}
            />

            {enableMaxUses && (
              <TextField
                fullWidth
                type="number"
                label="Maximum Uses"
                value={maxUses}
                onChange={(e) => setMaxUses(parseInt(e.target.value, 10) || 25)}
                inputProps={{ min: 1 }}
                sx={{ mb: 2 }}
              />
            )}
          </Box>
        ) : (
          // Link generated - show success
          <Box sx={{ pt: 1 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              {copied ? 'Link copied to clipboard!' : 'Public link created successfully!'}
            </Alert>

            <TextField
              fullWidth
              label="Share Link"
              value={generatedLink.fullUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopyLink} edge="end">
                      <CopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {generatedLink.expiresAt && (
                <Typography variant="body2" color="text.secondary">
                  Expires: {new Date(generatedLink.expiresAt).toLocaleString()}
                </Typography>
              )}
              {generatedLink.hasPassword && (
                <Typography variant="body2" color="text.secondary">
                  🔒 Password protected
                </Typography>
              )}
              {generatedLink.maxUses > 0 && (
                <Typography variant="body2" color="text.secondary">
                  📊 Limited to {generatedLink.maxUses} uses
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {generatedLink ? 'Close' : 'Cancel'}
        </Button>
        {!generatedLink && (
          <Button
            onClick={handleGenerateLink}
            variant="contained"
            disabled={loading || (enablePassword && !isPasswordValid)}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Creating...' : 'Create & Copy Link'}
          </Button>
        )}
        {generatedLink && (
          <Button
            onClick={handleCopyLink}
            variant="contained"
            startIcon={<CopyIcon />}
          >
            Copy Link Again
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShareDialog;
